/**
 * 商品知識の充足（横断）。
 *
 * ## 「まだ 1 件も生成していない」と「0 % 埋まっている」を書き分ける
 *
 * dev のこのテナントでは、商品画像からの機械生成がまだ 1 件も走っていない。
 * 属性の充足は正しく 0 % になるが、その数字だけを大きく出すと
 * 「属性を入れそこねている」と読まれる。そこで素材の件数
 * （`sources` の `semantics` ＝ 機械生成の行がある商品の件数）を必ず併記し、
 * それが 0 のときは**充足の数字より先に**その事実を断る。
 *
 * ## 数えていないものを 0 % と描かない
 *
 * `filled` / `total` / `coverage` が null なのは「数えていない」であって
 * 0 件ではない。割合の代わりに `dataGap` をそのまま出す。
 *
 * ## 1 画面 1 母数
 *
 * 数字はすべて `basis` の母集団のもの。母数を配信対象に切り替えると
 * 機械生成の属性群は数えられなくなるので、切り替える前に断る。
 */

import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "urql";
import {
  Card,
  Disclosure,
  ErrorState,
  KnowledgeCoverageDocument,
  LoadingState,
  Meter,
  PageHeader,
  Stat,
  Tag,
  type PopulationId,
  type ValueSource,
} from "visumo-data-agent-web-shared";

import {
  DEFAULT_KNOWLEDGE_POPULATION,
  KNOWLEDGE_DELIVERED_POPULATION_NOTE,
  KNOWLEDGE_GENERATED_SOURCE_KEY,
  KNOWLEDGE_GENERATION_IS_READ_ONLY_NOTE,
  KNOWLEDGE_NOT_GENERATED_NOTE,
} from "../api-capabilities";
import { formatJstWithZone } from "../format-time";
import { HelpIcon } from "../help/HelpIcon";
import { UgcPopulationSelect } from "./UgcPopulationSelect";

/** 属性の出所の表示名。NONE は「まだ取れていない」で、0 件ではない */
const SOURCE_LABEL: Record<ValueSource, string> = {
  MASTER: "商品マスター",
  REVIEW: "レビュー",
  UGC: "UGC",
  VIDEO: "動画",
  INFERRED: "商品画像からの機械生成",
  MANUAL: "人手の確定",
  NONE: "まだ取れていません",
};

/** 割合の描き方。null は「—」。0 % と書き分ける */
function percent(rate: number | null | undefined): string {
  return rate == null ? "—" : `${Math.round(rate * 100)} %`;
}

function count(value: number | null | undefined): string {
  return value == null ? "—" : value.toLocaleString("ja-JP");
}

export function KnowledgeCoveragePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const order = searchParams.get("order") === "filled" ? "filled" : "missing";
  const [population, setPopulation] = useState<PopulationId>(
    DEFAULT_KNOWLEDGE_POPULATION,
  );
  const [minUgcCount, setMinUgcCount] = useState(50);
  const [result, refetch] = useQuery({
    query: KnowledgeCoverageDocument,
    variables: {
      population,
      minUgcCount: population === "WITH_UGC" ? minUgcCount : null,
    },
  });

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const coverage = result.data.knowledgeCoverage;
  const sortedGroups = [...coverage.groups].sort((a, b) => {
    if (a.coverage === null) return b.coverage === null ? 0 : 1;
    if (b.coverage === null) return -1;
    return order === "filled"
      ? b.coverage - a.coverage
      : a.coverage - b.coverage;
  });
  const generated =
    coverage.sources.find(
      (source) => source.key === KNOWLEDGE_GENERATED_SOURCE_KEY,
    ) ?? null;
  // **「数えたら 0 件」だけを断る。** 数えていない（null）ときに
  // 「まだ走っていません」と書くと、数えていないことが消える
  const nothingGenerated = generated !== null && generated.count === 0;
  const countedGroups = coverage.groups.filter(
    (group) => group.coverage !== null,
  );
  const uncountedGroups = coverage.groups.filter(
    (group) => group.coverage === null,
  );

  return (
    <div className="vd-page">
      <PageHeader
        title="売るために要る属性が、どれだけ埋まっているか"
        description="属性群ごとに、値が入っている商品の件数を数えています。数えていないものは割合を出さず、理由を出します。"
        titleExtra={<HelpIcon slug="knowledge/coverage" />}
      />

      <div className="vd-stack">
        <Card
          title="どの商品を母数にするか"
          note={KNOWLEDGE_DELIVERED_POPULATION_NOTE}
        >
          <UgcPopulationSelect
            population={population}
            minimum={minUgcCount}
            onChange={(next, minimum) => {
              setPopulation(next);
              setMinUgcCount(minimum);
            }}
          />
        </Card>

        {nothingGenerated ? (
          <div className="vd-notice" role="status">
            <p style={{ margin: 0 }}>
              <strong>{KNOWLEDGE_NOT_GENERATED_NOTE}</strong>
            </p>
          </div>
        ) : null}

        <Card
          title="この母数の全体"
          note={`この画面の数字はすべて「${coverage.basis.label}」を母数にしています。${coverage.basis.note}`}
        >
          <div className="vd-grid">
            <Stat
              label="数えた商品"
              value={count(coverage.productCount)}
              note="商品マスターに行がある商品の件数"
            />
            <Stat
              label="機械生成の行がある商品"
              value={count(generated?.count)}
              note={
                generated === null ? "この素材は返っていません" : generated.note
              }
            />
            <Stat
              label="割合を出せた属性群"
              value={`${countedGroups.length} / ${coverage.groups.length}`}
              note="残りは数えていない属性群です"
            />
          </div>
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            集計した時刻: {formatJstWithZone(coverage.computedAt)}
          </p>
          {coverage.dataGap === null ? null : (
            <Disclosure
              summary={`「${coverage.dataGap.what}」を数えていない理由`}
            >
              <p style={{ margin: 0 }}>{coverage.dataGap.why}</p>
              <p style={{ margin: "4px 0 0" }}>
                必要なもの: {coverage.dataGap.needs}
                {coverage.dataGap.external === null
                  ? ""
                  : `（接続先: ${coverage.dataGap.external}）`}
              </p>
            </Disclosure>
          )}
        </Card>

        <Card
          title="属性の機械生成の状態"
          note={KNOWLEDGE_GENERATION_IS_READ_ONLY_NOTE}
        >
          <div className="vd-grid">
            <Stat
              label="最後に生成できた時刻"
              value={
                coverage.generation.lastGeneratedAt === null
                  ? "—"
                  : formatJstWithZone(coverage.generation.lastGeneratedAt)
              }
              note={
                coverage.generation.lastGeneratedAt === null
                  ? "まだ 1 件も走っていません"
                  : "成功した生成のうち、いちばん新しいものです"
              }
            />
            <Stat
              label="生成できた商品"
              value={`${count(coverage.generation.succeededCount)} / ${count(
                coverage.generation.productCount,
              )}`}
              note="属性の行がある商品の件数です"
            />
            <Stat
              label="失敗した商品"
              value={count(coverage.generation.failedCount)}
              note={
                coverage.generation.failedCount === null
                  ? "数えていません（0 件という意味ではありません）"
                  : "生成に失敗した商品の件数です"
              }
            />
          </div>
          {coverage.generation.failedDataGap === null ? null : (
            <Disclosure
              summary={`「${coverage.generation.failedDataGap.what}」を数えていない理由`}
            >
              <p style={{ margin: 0 }}>
                {coverage.generation.failedDataGap.why}
              </p>
              <p style={{ margin: "4px 0 0" }}>
                必要なもの: {coverage.generation.failedDataGap.needs}
                {coverage.generation.failedDataGap.external === null
                  ? ""
                  : `（接続先: ${coverage.generation.failedDataGap.external}）`}
              </p>
            </Disclosure>
          )}
        </Card>

        <Card
          title="属性群ごとの充足"
          note="「—」は数えていないという意味で、0 % とは別のことです。軸が無い属性群は、集めに行くのではなく軸を作るのが次の一手になります。"
        >
          <label
            className="vd-field"
            style={{ maxWidth: 200, marginBottom: 12 }}
          >
            <span>充足の並び順</span>
            <select
              className="vd-select"
              value={order}
              onChange={(event) => {
                const next = new URLSearchParams(searchParams);
                next.set("order", event.target.value);
                setSearchParams(next);
              }}
            >
              <option value="missing">不足順</option>
              <option value="filled">充足順</option>
            </select>
          </label>
          <div className="vd-table-scroll">
            <table className="vd-table" aria-label="属性群ごとの充足">
              <thead>
                <tr>
                  <th>属性群</th>
                  <th>統制語彙の軸</th>
                  <th>埋まっている属性</th>
                  <th>充足</th>
                  <th>出所</th>
                </tr>
              </thead>
              <tbody>
                {sortedGroups.map((group) => (
                  <tr key={group.name}>
                    <th scope="row">{group.name}</th>
                    <td>
                      {group.axis === null ? (
                        <Tag>軸なし（値域を閉じていません）</Tag>
                      ) : (
                        group.axis
                      )}
                    </td>
                    <td>
                      {group.filled == null || group.total == null
                        ? "数えていません"
                        : `${group.filled.toLocaleString("ja-JP")} / ${group.total.toLocaleString("ja-JP")}`}
                    </td>
                    <td>
                      <div>{percent(group.coverage)}</div>
                      {group.coverage === null ? null : (
                        <Meter ratio={group.coverage} />
                      )}
                    </td>
                    {/*
                      出所は属性 1 件ごとに返るので、同じ出所が属性の数だけ
                      並ぶ。畳まないと「商品マスター / 商品マスター /
                      商品マスター」と出て、3 つの出所があるように読める
                    */}
                    <td>
                      {[...new Set(group.sources)]
                        .map((source) => SOURCE_LABEL[source])
                        .join(" / ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {uncountedGroups.length === 0 ? null : (
            <div style={{ marginTop: 12 }}>
              <Disclosure
                summary={`数えていない属性群が ${uncountedGroups.length} 群あります`}
              >
                {uncountedGroups.map((group) => (
                  <p key={group.name} style={{ margin: "0 0 4px" }}>
                    <strong>{group.name}</strong>:{" "}
                    {group.dataGap === null
                      ? "理由が返っていません"
                      : `${group.dataGap.why}（必要なもの: ${group.dataGap.needs}）`}
                  </p>
                ))}
              </Disclosure>
            </div>
          )}
        </Card>

        <Card
          title="埋まった値はどこから来るか"
          note="件数はこの母数全体のもので、商品 1 件の内訳ではありません。数えていない素材は「—」で、0 件とは書き分けています。"
        >
          <div className="vd-table-scroll">
            <table className="vd-table" aria-label="素材ごとの件数">
              <thead>
                <tr>
                  <th>素材</th>
                  <th>行がある商品</th>
                  <th>母数に対する割合</th>
                  <th>注記</th>
                </tr>
              </thead>
              <tbody>
                {coverage.sources.map((source) => (
                  <tr key={source.key}>
                    <th scope="row">{source.label}</th>
                    <td>{count(source.count)}</td>
                    <td>{percent(source.percent)}</td>
                    <td>{source.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card
          title="1 品目ずつ見る"
          note="属性の値と出所、人手で確定した版は、商品ごとの知識シートで見ます。"
        >
          <p style={{ margin: 0 }}>
            <Link to="/products">商品一覧</Link>{" "}
            から商品を選ぶと、その商品の知識シートを開けます。
          </p>
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            属性スキーマと統制語彙は{" "}
            <Link to="/knowledge/schema">属性スキーマと統制語彙</Link>{" "}
            で参照できます。
          </p>
        </Card>
      </div>
    </div>
  );
}
